/**
 * 导航栏组件
 * 负责：导航菜单、用户信息、学院/专业选择、登出
 */
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import {
  Avatar,
  Cascader,
  Dropdown,
  Menu,
  Divider,
  Button,
} from '@arco-design/web-react';
import {
  IconSettings,
  IconPoweroff,
  IconUser,
  IconApps,
  IconCommon,
  IconLiveBroadcast,
  IconShareAlt,
} from '@arco-design/web-react/icon';
import { useAppStore } from '@/store/appStore';
import useLocale from '@/utils/useLocale';
import Logo from '@/assets/logo.svg';
import Settings from '../Settings';
import styles from './style/index.module.less';
import useStorage from '@/utils/useStorage';
import { IRoute } from '@/routes';
import { getTreeApi } from '@/request/api';
import { useHistory, useLocation } from 'react-router-dom';
import {
  umamiTrack,
  clearKouziAccessToken,
  clearKouziRefreshToken,
} from '@/utils/index';

// 导航栏组件
function Navbar({ show, routes, onClickMenuItem }: {
  show: boolean;
  routes?: IRoute[];
  onClickMenuItem?: (key: string) => void;
}) {
  const t = useLocale();
  const { userInfo, userLoading } = useAppStore((state) => ({
    userInfo: state.global.userInfo,
    userLoading: state.global.userLoading,
  }));
  const setProfession = useAppStore((state) => state.setProfession);
  const history = useHistory();
  const location = useLocation();

  // 存储用户状态
  const [_, setUserStatus] = useStorage('userStatus');

  // 学院/专业数据和选中值
  const [dept, setDept] = useState([]);
  const [deptValues, setDeptValues] = useState([]);

  // 菜单相关状态
  const [selectedFirstLevel, setSelectedFirstLevel] = useState<string>('');
  const [selectedSecondLevel, setSelectedSecondLevel] = useState<string>('');

  // 从 userInfo 获取用户名
  const userName = userInfo?.name || '';

  // 处理学院/专业数据并设置默认值
  const handleDeptData = useCallback((deptList) => {
    const savedCollegeId = localStorage.getItem('sysDeptId');
    const savedMajorId = localStorage.getItem('sysSpecialityDeptId');

    const firstCollege = deptList[0];
    const firstMajor = firstCollege?.children?.[0];

    const finalCollege =
      deptList.find((c) => String(c.id) === savedCollegeId) || firstCollege;
    const finalMajor =
      finalCollege?.children?.find((m) => String(m.id) === savedMajorId) ||
      firstMajor;

    setDept(deptList);
    setDeptValues([finalCollege?.id, finalMajor?.id].filter(Boolean));

    setProfession({
      sysDeptId: finalCollege?.id,
      sysSpecialityDeptId: finalMajor?.id,
    });
  }, [setProfession]);

  // 初始化函数：获取学院专业数据
  const initializeData = useCallback(async () => {
    try {
      const deptRes = await getTreeApi();
      handleDeptData(deptRes.data || []);
    } catch (error) {
      console.error('获取学院专业数据失败:', error);
    }
  }, [handleDeptData]);

  // 初始化：加载学院专业数据
  useEffect(() => {
    initializeData();
  }, [initializeData]);

  // 学院/专业切换处理
  const onCascaderChange = useCallback((value) => {
    if (!value || value.length < 2) return;

    const [collegeId, majorId] = value;
    setDeptValues(value);
    localStorage.setItem('sysDeptId', collegeId);
    localStorage.setItem('sysSpecialityDeptId', majorId);

    setProfession({
      sysDeptId: collegeId,
      sysSpecialityDeptId: majorId,
    });
  }, [setProfession]);

  // 登出处理
  const handleLogout = useCallback(() => {
    umamiTrack(window, '登出', {
      nodeType: '\\',
      nodeName: '登出',
    });
    clearKouziAccessToken();
    clearKouziRefreshToken();
    setUserStatus('logout');
    window.location.href = '/login';
  }, [setUserStatus]);

  // 头像下拉菜单点击处理
  const handleMenuClick = useCallback((key: string) => {
    if (key === 'logout') {
      handleLogout();
    } else if (key === 'personal') {
      history.push('/system/personal');
    }
  }, [handleLogout, history]);

  // 匹配路由（提取为独立函数）
  const matchRoute = useCallback((path: string, hash: string, routes: IRoute[]) => {
    let matchedFirstLevel: IRoute | null = null;
    let matchedSecondLevel: IRoute | null = null;

    for (const route of routes) {
      if (route.ignore) continue;

      // 检查一级路由
      if (path === route.key || path.startsWith(`${route.key}/`)) {
        matchedFirstLevel = route;

        // 检查子路由
        if (route.children?.length) {
          const visibleChildren = route.children.filter(child => !child.ignore);

          for (const child of visibleChildren) {
            if (child.key.includes('#')) {
              const [childPath, childHash] = child.key.split('#');
              if (path === childPath && hash === childHash) {
                matchedSecondLevel = child;
                break;
              }
            } else if (path === child.key || path.startsWith(`${child.key}/`)) {
              matchedSecondLevel = child;
              break;
            }
          }

          // 如果没有匹配到hash路由且当前无hash，选择第一个非hash子菜单
          if (!matchedSecondLevel && !hash) {
            matchedSecondLevel = visibleChildren.find(child => !child.key.includes('#')) || visibleChildren[0];
          }
        }
        break;
      }

      // 检查子路由是否直接匹配
      if (route.children) {
        const visibleChildren = route.children.filter(child => !child.ignore);
        for (const child of visibleChildren) {
          if (child.key.includes('#')) {
            const [childPath, childHash] = child.key.split('#');
            if (path === childPath && hash === childHash) {
              matchedFirstLevel = route;
              matchedSecondLevel = child;
              break;
            }
          } else if (path === child.key || path.startsWith(`${child.key}/`)) {
            matchedFirstLevel = route;
            matchedSecondLevel = child;
            break;
          }
        }
        if (matchedFirstLevel) break;
      }
    }

    return { matchedFirstLevel, matchedSecondLevel };
  }, []);

  // 根据当前路径和hash计算选中的菜单
  useEffect(() => {
    if (!routes || !location.pathname) return;

    const path = location.pathname.slice(1);
    const hash = location.hash.slice(1);
    const { matchedFirstLevel, matchedSecondLevel } = matchRoute(path, hash, routes);

    if (matchedFirstLevel) {
      setSelectedFirstLevel(matchedFirstLevel.key);
      if (matchedSecondLevel) {
        setSelectedSecondLevel(matchedSecondLevel.key);
      } else {
        const visibleChildren = matchedFirstLevel.children?.filter(child => !child.ignore) || [];
        setSelectedSecondLevel(visibleChildren[0]?.key || '');
      }
    } else {
      // 宽松匹配
      const directRoute = routes.find(route => !route.ignore && path.includes(route.key));
      if (directRoute) {
        setSelectedFirstLevel(directRoute.key);
        setSelectedSecondLevel('');
      }
    }
  }, [location.pathname, location.hash, routes, matchRoute]);


  // 一级菜单点击处理
  const handleFirstLevelClick = useCallback((route: IRoute) => {
    setSelectedFirstLevel(route.key);
    const visibleChildren = route.children?.filter(child => !child.ignore) || [];

    if (visibleChildren.length > 0) {
      const firstChild = visibleChildren[0];
      setSelectedSecondLevel(firstChild.key);
      onClickMenuItem?.(firstChild.key);
    } else {
      setSelectedSecondLevel('');
      onClickMenuItem?.(route.key);
    }
  }, [onClickMenuItem]);

  // 二级菜单点击处理
  const handleSecondLevelClick = useCallback((route: IRoute) => {
    setSelectedSecondLevel(route.key);

    if (route.key.includes('#')) {
      const [path, hash] = route.key.split('#');
      const currentPath = location.pathname.slice(1);

      if (currentPath === path) {
        history.replace(`/${path}#${hash}`);
      } else {
        onClickMenuItem?.(path);
        setTimeout(() => {
          history.push(`/${path}#${hash}`);
        }, 800);
      }
    } else {
      onClickMenuItem?.(route.key);
    }
  }, [onClickMenuItem, location.pathname, history]);

  // 三级菜单点击处理
  const handleThirdLevelClick = useCallback((route: IRoute) => {
    onClickMenuItem?.(route.key);
  }, [onClickMenuItem]);

  // 渲染三级菜单下拉框
  const renderThirdLevelMenu = useCallback((children: IRoute[]) => {
    const visibleChildren = children.filter(child => !child.ignore);
    if (visibleChildren.length === 0) return null;

    const menuItems = visibleChildren.map(child => (
      <Menu.Item key={child.key} onClick={() => handleThirdLevelClick(child)}>
        {t[child.name] || child.name}
      </Menu.Item>
    ));

    return (
      <Menu>
        {menuItems}
      </Menu>
    );
  }, [handleThirdLevelClick, t]);

  // 计算当前选中一级菜单的二级菜单
  const currentSecondLevelMenus = useMemo(() => {
    if (!selectedFirstLevel || !routes) return [];
    const firstLevelRoute = routes.find(route => route.key === selectedFirstLevel);
    if (!firstLevelRoute || !firstLevelRoute.children) return [];
    return firstLevelRoute.children.filter(child => !child.ignore);
  }, [selectedFirstLevel, routes]);

  // 获取一级菜单列表（排除ignore的）
  const firstLevelMenus = useMemo(() => {
    if (!routes) return [];
    return routes.filter(route => !route.ignore);
  }, [routes]);

  // 当不需要显示完整导航栏时，仅显示设置按钮
  if (!show) {
    return (
      <div className={styles['fixed-settings']}>
        <Settings
          trigger={
            <Button icon={<IconSettings />} type="primary" size="large" />
          }
        />
      </div>
    );
  }

  // 用户头像下拉菜单
  const userMenu = (
    <Menu onClickMenuItem={handleMenuClick}>
      <Divider style={{ margin: '4px 0' }} />
      <Menu.Item key="personal">
        <IconUser className={styles['dropdown-icon']} />
        {t['menu.system.personal']}
      </Menu.Item>
      <Menu.Item key="logout">
        <IconPoweroff className={styles['dropdown-icon']} />
        {t['navbar.logout']}
      </Menu.Item>
    </Menu>
  );

  return (
    <div className={styles.navbar}>
      {/* 第一排：Logo + 一级菜单 + 右侧操作 */}
      <div className={styles['navbar-first-row']}>
        <div className={styles.left}>
          <div className={styles.logo}>
            <Logo />
            <div className={styles['logo-name']}>智慧教育</div>
          </div>
        </div>

        {/* 一级菜单 */}
        <div className={styles['first-level-menu']}>
          {firstLevelMenus.map((route) => (
            <div
              key={route.key}
              className={`${styles['menu-item']} ${selectedFirstLevel === route.key ? styles['menu-item-active'] : ''
                }`}
              onClick={() => handleFirstLevelClick(route)}
            >
              <span>{t[route.name] || route.name}</span>
            </div>
          ))}
        </div>

        <ul className={styles.right}>
          {/* 学院/专业选择器 */}
          <li>
            <Cascader
              style={{ width: '260px' }}
              placeholder="请选择学院/专业"
              options={dept}
              value={deptValues}
              onChange={onCascaderChange}
              fieldNames={{
                label: 'name',
                value: 'id',
                children: 'children',
              }}
            />
          </li>

          {/* 用户头像与下拉菜单 */}
          {userInfo && (
            <li>
              <Dropdown droplist={userMenu} position="br" disabled={userLoading}>
                <Avatar
                  size={32}
                  style={{ cursor: 'pointer', backgroundColor: '#00d0b6' }}
                >
                  {userName ? userName.slice(-2) : ''}
                </Avatar>
              </Dropdown>
            </li>
          )}
        </ul>
      </div>

      {/* 第二排：二级菜单 - 悬浮样式 */}
      {selectedFirstLevel && currentSecondLevelMenus.length > 0 && (
        <div className={styles['navbar-second-row']}>
          {currentSecondLevelMenus.map((route) => {
            const hasThirdLevel = route.children && route.children.filter(child => !child.ignore).length > 0;

            if (hasThirdLevel) {
              // 有三级菜单，使用下拉框
              return (
                <Dropdown
                  key={route.key}
                  droplist={renderThirdLevelMenu(route.children || [])}
                  position="bl"
                  trigger="click"
                >
                  <div
                    className={`${styles['second-menu-item']} ${selectedSecondLevel === route.key ? styles['second-menu-item-active'] : ''
                      }`}
                    onClick={(e) => {
                      e.stopPropagation();
                      // 如果有三级菜单，点击时只选中但不跳转
                      setSelectedSecondLevel(route.key);
                    }}
                  >
                    {t[route.name] || route.name}
                    <span className={styles['dropdown-arrow']}>▼</span>
                  </div>
                </Dropdown>
              );
            } else {
              // 没有三级菜单，直接点击
              return (
                <div
                  key={route.key}
                  className={`${styles['second-menu-item']} ${selectedSecondLevel === route.key ? styles['second-menu-item-active'] : ''
                    }`}
                  onClick={() => handleSecondLevelClick(route)}
                >
                  {t[route.name] || route.name}
                </div>
              );
            }
          })}
        </div>
      )}
    </div>
  );
}

export default Navbar;
